import { ReactNode } from "react";
import {
  alpha,
  Avatar,
  Box,
  Button,
  darken,
  lighten,
  Typography,
  useTheme,
} from "@mui/material";
import { ReactComponent as CaixaVazia } from "../../assets/easyReport/iconSvg/emptyBox.svg";
import {
  initials,
  nameToColorKey,
  STATUS_BY_KEY,
  StatusKey,
} from "../../pages/suporte/types";
import { styless } from "../../pages/suporte/styles";

interface StatusPillProps {
  status: StatusKey;
  tamanho?: "sm" | "md";
}

export function StatusPill({ status, tamanho = "md" }: StatusPillProps) {
  const theme = useTheme();
  const meta = STATUS_BY_KEY[status];
  if (!meta) return null;

  const dark = theme.palette.mode === "dark";
  const cor = theme.palette.chip[meta.chipKey];
  const texto = dark ? lighten(cor, 0.62) : darken(cor, 0.18);
  const ponto = dark ? lighten(cor, 0.32) : cor;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.7,
        height: tamanho === "sm" ? 22 : 26,
        px: tamanho === "sm" ? 1.1 : 1.35,
        borderRadius: 999,
        fontSize: tamanho === "sm" ? "0.7rem" : "0.745rem",
        fontWeight: 700,
        letterSpacing: "0.01em",
        whiteSpace: "nowrap",
        color: texto,
        backgroundColor: alpha(cor, dark ? 0.38 : 0.15),
        border: `1px solid ${alpha(cor, dark ? 0.6 : 0.34)}`,
      }}
    >
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          flexShrink: 0,
          backgroundColor: ponto,
          boxShadow: `0 0 0 3px ${alpha(ponto, 0.22)}`,
        }}
      />
      {meta.label}
    </Box>
  );
}

interface UserAvatarProps {
  nome: string;
  foto?: string;
  tamanho?: number;
}

export function UserAvatar({ nome, foto, tamanho = 30 }: UserAvatarProps) {
  const theme = useTheme();
  const cor = theme.palette.cromatic[nameToColorKey(nome || "?")];

  return (
    <Avatar
      src={foto}
      sx={{
        width: tamanho,
        height: tamanho,
        flexShrink: 0,
        fontSize: tamanho * 0.36,
        fontWeight: 700,
        bgcolor: cor.dark,
        color: "#fff",
      }}
    >
      {initials(nome || "?")}
    </Avatar>
  );
}

interface EmptyStateProps {
  titulo: string;
  texto?: string;
  acao?: { label: string; onClick: () => void };
  icone?: ReactNode;
}

export function EmptyState({ titulo, texto, acao, icone }: EmptyStateProps) {
  const theme = useTheme();
  const styles = styless(theme);

  return (
    <Box sx={styles.vazio}>
      {icone ?? (
        <CaixaVazia
          style={{
            width: 110,
            height: 110,
            opacity: 0.5,
            color: theme.palette.text.secondary,
          }}
        />
      )}
      <Box>
        <Typography sx={styles.vazioTitulo}>{titulo}</Typography>
        {texto && <Typography sx={styles.vazioTexto}>{texto}</Typography>}
      </Box>
      {acao && (
        <Button
          variant="outlined"
          size="small"
          onClick={acao.onClick}
          sx={{ textTransform: "none", borderRadius: 1.5 }}
        >
          {acao.label}
        </Button>
      )}
    </Box>
  );
}
